import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;



// Enhanced Footer with Rich Visual Effects
const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #0a1628 0%, #152238 50%, #1a2744 100%);
  color: #f8f9fa;
  padding: 50px 0 24px;
  margin-top: auto;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, rgba(201,169,110,0.5), rgba(201,169,110,1), rgba(201,169,110,0.5));
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(108, 117, 125, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(73, 80, 87, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FooterContent = styled.div`
  max-width: none;
  margin: 0;
  padding: 0 5%;
  position: relative;
  z-index: 2;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 35px;
  margin-bottom: 40px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const FooterSection = styled.div`
  animation: ${fadeInUp} 0.8s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
  
  h3 {
    color: #f8f9fa;
    margin-bottom: 20px;
    font-size: 1.15rem;
    font-weight: 500;
    position: relative;
    letter-spacing: -0.5px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 2px;
      background: linear-gradient(90deg, #c9a96e, transparent);
      border-radius: 1px;
    }
  }
  
  p {
    line-height: 1.6;
    margin-bottom: 12px;
    color: #adb5bd;
    
    transition: color 0.3s ease;
    
    &:hover {
      color: #dee2e6;
    }
  }
  
  ul {
    li {
      margin-bottom: 8px;
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateX(5px);
      }
      
      a {
        color: #adb5bd;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        padding: 4px 0;
        
        display: inline-block;
        
        &::before {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 0;
          height: 1px;
          background: linear-gradient(90deg, #c9a96e, transparent);
          transition: width 0.4s ease;
        }
        
        &:hover {
          color: #f8f9fa;
          padding-left: 10px;
        }
        
        &:hover::before {
          width: 100%;
        }
      }
    }
  }
`;

// Company Info Section with Enhanced Styling
const CompanySection = styled(FooterSection)`
  .logo {
    font-size: 1.6rem;
    font-weight: 700;
    color: #f8f9fa;
    margin-bottom: 14px;
    letter-spacing: -1px;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 3px;
      background: linear-gradient(90deg, #6c757d, transparent);
      border-radius: 2px;
    }
  }
  
  .description {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #adb5bd;
    margin-bottom: 16px;
    
  }
  
  .contact-info {
    margin-top: 16px;
    
    .contact-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      color: #adb5bd;
      transition: all 0.3s ease;
      
      &:hover {
        color: #f8f9fa;
        transform: translateX(5px);
      }
      
      .icon {
        margin-right: 12px;
        font-size: 1.1rem;
        color: #6c757d;
        transition: color 0.3s ease;
      }
      
      &:hover .icon {
        color: #f8f9fa;
      }
    }
  }
`;



const DeveloperCredit = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 992px) {
    order: 3;
    width: 100%;
    margin-top: 20px;
  }
  
  p {
    font-size: 11px;
    letter-spacing: 0.15em;
    color: rgba(255, 255, 255, 0.4);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    text-transform: none;
  }

  .line {
    display: none;
    width: 24px;
    height: 1px;
    background: rgba(255, 255, 255, 0.15);
    
    @media (min-width: 1200px) {
      display: inline-block;
    }
  }

  .code-icon {
    color: rgba(34, 197, 94, 0.6);
    font-family: monospace;
    font-size: 10px;
  }

  a {
    color: #22C55E;
    text-decoration: none;
    transition: all 0.5s ease;
    font-weight: 700;
    letter-spacing: -0.025em;
    text-transform: uppercase;
    font-size: 12px;
    display: inline-block;
    padding: 0;
    
    &:hover {
      color: #4ADE80;
    }
  }
`;
const FooterBottom = styled.div`
  border-top: 1px solid rgba(108, 117, 125, 0.2);
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  .copyright {
    flex: 1;
    color: #6c757d;
    font-size: 0.95rem;
    
    
    .year {
      font-weight: 500;
      color: #adb5bd;
    }
  }

  .footer-links {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    gap: 30px;
    
    @media (max-width: 768px) {
      gap: 20px;
    }
    
    a {
      color: #6c757d;
      font-size: 0.9rem;
      
      transition: all 0.3s ease;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -3px;
        left: 0;
        width: 0;
        height: 1px;
        background: #adb5bd;
        transition: width 0.3s ease;
      }
      
      &:hover {
        color: #adb5bd;
      }
      
      &:hover::after {
        width: 100%;
      }
    }
  }
`;

const Footer = () => {
  const { t } = useLanguage();
  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <CompanySection delay="0.1s">
            <div className="logo">{t('brand.name')}</div>
            <p className="description">
              {t('footer.description')}
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <span className="icon">📞</span>
                <span><bdi>0532 131 63 30</bdi></span>
              </div>
              <div className="contact-item">
                <span className="icon">📧</span>
                <span>info@dmrcimento.com</span>
              </div>
            </div>

          </CompanySection>
          
          <FooterSection delay="0.2s">
            <h3>{t('footer.corporate')}</h3>
            <ul>
              <li><Link to="/hakkimizda">{t('footer.about')}</Link></li>
              <li><Link to="/hakkimizda/vizyon">{t('footer.visionMission')}</Link></li>
              <li><Link to="/hakkimizda/degerler">{t('footer.coreValues')}</Link></li>
              <li><Link to="/hakkimizda/kalite">{t('footer.qualityPolicy')}</Link></li>
              <li><Link to="/hakkimizda/insan-kaynaklari">{t('footer.hr')}</Link></li>
            </ul>
          </FooterSection>
          
          
          <FooterSection delay="0.3s">
            <h3>{t('footer.services')}</h3>
            <ul>

              <li><Link to="/hizmetler/teknik-destek">{t('footer.technicalSupport')}</Link></li>
              <li><Link to="/hizmetler/kalite-kontrol">{t('footer.qualityControl')}</Link></li>
              <li><Link to="/hizmetler/lojistik">{t('footer.logistics')}</Link></li>
              <li><Link to="/hizmetler/danismanlik">{t('footer.consulting')}</Link></li>
            </ul>
          </FooterSection>
          
          <FooterSection delay="0.4s">
            <h3>{t('footer.support')}</h3>
            <ul>
              <li><Link to="/iletisim">{t('footer.contact')}</Link></li>
              <li><Link to="/hakkimizda/sss">{t('footer.faq')}</Link></li>
              <li><Link to="/hakkimizda/gizlilik">{t('footer.privacyPolicy')}</Link></li>
              <li><Link to="/hakkimizda/sartlar">{t('footer.termsConditions')}</Link></li>
              <li><Link to="/hizmetler/teknik-dokumantasyon">{t('footer.technicalDocs')}</Link></li>
            </ul>
          </FooterSection>
        </FooterTop>
        
        <FooterBottom>
          <div className="copyright">
            © <span className="year">2025</span> {t('footer.copyright')}
          </div>

          <DeveloperCredit>
            <p>
              <span className="line"></span>
              <span className="code-icon">&lt;/&gt;</span>
              <span>{t('footer.designedBy')}</span>
              <a href="https://passyazilim.com" target="_blank" rel="noopener noreferrer">Pass Yazılım</a>
              <span className="line"></span>
            </p>
          </DeveloperCredit>

          <div className="footer-links">
            <Link to="/hakkimizda/gizlilik">{t('footer.privacy')}</Link>
            <Link to="/hakkimizda/sartlar">{t('footer.terms')}</Link>
            <Link to="/iletisim">{t('footer.contact')}</Link>
          </div>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;